import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import logger from '../utils/logger';
import { UPLOADS_URL, BACKEND_URL } from '../config';
import { Rocket, Search, Sparkles, Target, Share2, ClipboardList, Copy, User, Lock, Plus, Briefcase, Store, Globe, Puzzle, Star, Check, X, Heart, Calendar, Coins, TrendingUp, Folder, CheckCircle, CheckCircle2, Clock, XCircle, RefreshCw, Package, Building2, Handshake, GraduationCap, ShoppingCart, Laptop, Wrench, Wheat, Home, Truck, UtensilsCrossed, Scissors, Pill, BookOpen, Shuffle, Menu, LayoutGrid } from 'lucide-react';

// Fonction pour mélanger un tableau (Fisher-Yates shuffle)
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Composant Image avec gestion d'erreur
const ProjectImage = ({ src, alt, className, fallbackIcon, fallbackGradient }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  if (!src || hasError) {
    return (
      <div className={`${className} ${fallbackGradient} flex items-center justify-center`}>
        <span className="opacity-50">{typeof fallbackIcon === 'string' ? <span className="text-5xl">{fallbackIcon}</span> : React.cloneElement(fallbackIcon, { size: 48 })}</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`${className} ${isLoaded ? '' : 'opacity-0'}`}
      onError={() => setHasError(true)}
      onLoad={() => setIsLoaded(true)}
    />
  );
};

const FeedPourToi = () => {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [displayedTasks, setDisplayedTasks] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('random');
  const [viewMode, setViewMode] = useState('grid');
  const [hasMore, setHasMore] = useState(true);
  const [showAuthorModal, setShowAuthorModal] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const tasksPerLoad = 12;
  
  // États pour le panneau des modules
  const [modules, setModules] = useState([]);
  const [showModulesPanel, setShowModulesPanel] = useState(false);
  const [moduleSearch, setModuleSearch] = useState('');
  const [favoriteModules, setFavoriteModules] = useState(() => {
    const saved = localStorage.getItem('djamann_favorite_modules');
    return saved ? JSON.parse(saved) : [];
  });
  const [showFavorites, setShowFavorites] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // États pour les favoris d'opportunités
  const [favoriteOpportunities, setFavoriteOpportunities] = useState(() => {
    const saved = localStorage.getItem('djamann_favorite_opportunities');
    return saved ? JSON.parse(saved) : [];
  });
  
  // État pour le modal de partage
  const [shareModal, setShareModal] = useState({ show: false, task: null });
  const [copySuccess, setCopySuccess] = useState(false);
  
  // État pour filtrer les favoris uniquement
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);

  // Fonction de recherche intelligente - normalise les accents et fait une recherche floue
  const normalizeText = (text) => {
    if (!text) return '';
    return text.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
      .replace(/[^a-z0-9\s]/g, ' '); // Garde que lettres, chiffres, espaces
  };

  // Fonction de recherche intelligente avec scoring
  const smartSearch = (task, searchTerms) => {
    if (!searchTerms.length) return true;
    
    // Normaliser les champs de la tâche
    const taskTitle = normalizeText(task.titre);
    const taskDesc = normalizeText(task.description);
    const projectTitle = normalizeText(task.project_titre);
    const skills = normalizeText(task.competences_requises);
    const authorName = normalizeText(task.author_nom);
    const location = normalizeText(`${task.lieu_ville || ''} ${task.lieu_pays || ''}`);
    const category = normalizeText(task.project_categorie);
    
    // Parser les tags du projet (JSON)
    let tagsList = '';
    try {
      const tags = task.project_tags ? JSON.parse(task.project_tags) : [];
      tagsList = normalizeText(Array.isArray(tags) ? tags.join(' ') : '');
    } catch (e) {
      tagsList = '';
    }
    
    // Tous les champs recherchables combinés
    const allFields = `${taskTitle} ${taskDesc} ${projectTitle} ${skills} ${authorName} ${location} ${category} ${tagsList}`;
    
    // Vérifier si tous les termes de recherche correspondent
    return searchTerms.every(term => {
      // Recherche exacte
      if (allFields.includes(term)) return true;
      
      // Recherche floue (tolérance de 1 caractère de différence pour les mots > 3 caractères)
      if (term.length > 3) {
        const words = allFields.split(/\s+/);
        return words.some(word => {
          if (word.length < 3) return false;
          // Vérifie si le mot commence par le terme
          if (word.startsWith(term) || term.startsWith(word)) return true;
          // Distance de Levenshtein simplifiée (1 caractère de différence max)
          if (Math.abs(word.length - term.length) <= 1) {
            let diff = 0;
            const minLen = Math.min(word.length, term.length);
            for (let i = 0; i < minLen; i++) {
              if (word[i] !== term[i]) diff++;
              if (diff > 1) return false;
            }
            return diff <= 1;
          }
          return false;
        });
      }
      return false;
    });
  };
  const { user } = useContext(AuthContext);

  const canAccessWorkspace = user && (user.role === 'author' || user.role === 'admin');

  const handleWorkspaceClick = (e) => {
    if (!canAccessWorkspace) {
      e.preventDefault();
      setShowAuthorModal(true);
    }
  };
  
  const observerRef = useRef();
  
  const loadMoreTasks = useCallback(() => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    setTimeout(() => {
      const currentLength = displayedTasks.length;
      const nextTasks = filteredTasks.slice(currentLength, currentLength + tasksPerLoad);
      
      if (nextTasks.length > 0) {
        setDisplayedTasks(prev => [...prev, ...nextTasks]);
        setHasMore(currentLength + nextTasks.length < filteredTasks.length);
      } else {
        setHasMore(false);
      }
      setLoadingMore(false);
    }, 300);
  }, [loadingMore, hasMore, displayedTasks.length, filteredTasks, tasksPerLoad]);
  
  const lastTaskRef = useCallback(node => {
    if (loadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreTasks();
      }
    });
    
    if (node) observerRef.current.observe(node);
  }, [loadingMore, hasMore, loadMoreTasks]);

  useEffect(() => {
    loadTasks();
    if (user) {
      loadMyApplications();
      loadModules();
      checkSubscriptionExpiration();
    }
  }, [user]);
  
  // Vérifier l'expiration de l'abonnement et envoyer notification si nécessaire
  const checkSubscriptionExpiration = async () => {
    try {
      const res = await api.post('/subscriptions/check-expiration');
      logger.log('Check expiration result:', res.data);
    } catch (err) {
      logger.error('Check expiration error:', err.response?.data || err.message);
    }
  };
  
  // Charger les modules de l'utilisateur
  const loadModules = async () => {
    try {
      const res = await api.get('/subscriptions/modules');
      setModules(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      logger.error('Erreur chargement modules:', err);
      setModules([]);
    }
  };
  
  // Sauvegarder les favoris dans localStorage
  useEffect(() => {
    localStorage.setItem('djamann_favorite_modules', JSON.stringify(favoriteModules));
  }, [favoriteModules]);
  
  // Sauvegarder les favoris d'opportunités dans localStorage
  useEffect(() => {
    localStorage.setItem('djamann_favorite_opportunities', JSON.stringify(favoriteOpportunities));
  }, [favoriteOpportunities]);
  
  // Toggle favoris modules
  const toggleFavorite = (moduleId) => {
    setFavoriteModules(prev => {
      if (prev.includes(moduleId)) {
        return prev.filter(id => id !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  };
  
  // Toggle favoris opportunités
  const toggleFavoriteOpportunity = (taskId) => {
    setFavoriteOpportunities(prev => {
      if (prev.includes(taskId)) {
        return prev.filter(id => id !== taskId);
      } else {
        return [...prev, taskId];
      }
    });
  };
  
  // Vérifier si une opportunité est en favori
  const isOpportunityFavorite = (taskId) => favoriteOpportunities.includes(taskId);
  
  // Générer l'URL de partage
  const getShareUrl = (task) => {
    return `${window.location.origin}/project/${task.project_id}`;
  };
  
  // Partager sur WhatsApp
  const shareOnWhatsApp = (task) => {
    const text = `Opportunité: ${task.titre}\nProjet: ${task.project_titre}\n${task.remuneration ? `${Number(task.remuneration).toLocaleString()} XOF` : ''}\n\n${getShareUrl(task)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    setShareModal({ show: false, task: null });
  };
  
  // Copier le lien
  const copyShareLink = async (task) => {
    const text = `${task.titre} - ${task.project_titre}\n${getShareUrl(task)}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      // Fallback pour les anciens navigateurs
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };
  
  // Partager par email
  const shareByEmail = (task) => {
    const subject = `Opportunité: ${task.titre}`;
    const body = `Bonjour,\n\nJe voulais partager cette opportunité avec toi:\n\n${task.titre}\nProjet: ${task.project_titre}\n${task.description ? `\n${task.description.substring(0, 200)}...` : ''}\n${task.remuneration ? `\nRémunération: ${Number(task.remuneration).toLocaleString()} XOF` : ''}\n\nVoir l'opportunité: ${getShareUrl(task)}\n\nÀ bientôt !`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setShareModal({ show: false, task: null });
  };
  
  // Partager sur Facebook
  const shareOnFacebook = (task) => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl(task))}`, '_blank', 'width=600,height=400');
    setShareModal({ show: false, task: null });
  };
  
  // Partager sur Twitter/X
  const shareOnTwitter = (task) => {
    const text = `Opportunité: ${task.titre} - ${task.project_titre}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(getShareUrl(task))}`, '_blank', 'width=600,height=400');
    setShareModal({ show: false, task: null });
  };
  
  // Partager sur LinkedIn
  const shareOnLinkedIn = (task) => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getShareUrl(task))}`, '_blank', 'width=600,height=400');
    setShareModal({ show: false, task: null });
  };
  
  // Partager sur Telegram
  const shareOnTelegram = (task) => {
    const text = `Opportunité: ${task.titre}\nProjet: ${task.project_titre}`;
    window.open(`https://t.me/share/url?url=${encodeURIComponent(getShareUrl(task))}&text=${encodeURIComponent(text)}`, '_blank');
    setShareModal({ show: false, task: null });
  };
  
  // Filtrer les modules par recherche
  const filteredModules = modules.filter(m => 
    normalizeText(m.nom || '').includes(normalizeText(moduleSearch)) ||
    normalizeText(m.description || '').includes(normalizeText(moduleSearch)) ||
    normalizeText(m.code || '').includes(normalizeText(moduleSearch))
  );
  
  // Modules favoris avec leurs données complètes
  const favoriteModulesData = modules.filter(m => favoriteModules.includes(m.id));
  
  // Nettoyer le route_pattern (retirer /* à la fin)
  const cleanRoutePattern = (route) => {
    if (!route) return '#';
    return route.replace(/\/\*$/, '');
  };

  useEffect(() => {
    let filtered = [...tasks];

    // Filtre par favoris uniquement
    if (showOnlyFavorites) {
      filtered = filtered.filter(task => favoriteOpportunities.includes(task.id));
    }

    // Recherche intelligente avec tags
    if (search) {
      const searchTerms = normalizeText(search).split(/\s+/).filter(t => t.length > 0);
      filtered = filtered.filter(task => smartSearch(task, searchTerms));
    }

    // Filtre par type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(task => task.type === typeFilter);
    }

    // Filtre par catégorie
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(task => task.project_categorie === categoryFilter);
    }

    // Tri
    if (sortBy === 'random') {
      filtered = shuffleArray(filtered);
    } else if (sortBy === 'recent') {
      filtered.sort((a, b) => new Date(b.created_at || b.date_creation) - new Date(a.created_at || a.date_creation));
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => new Date(a.created_at || a.date_creation) - new Date(b.created_at || b.date_creation));
    }

    setFilteredTasks(filtered);
    setDisplayedTasks(filtered.slice(0, tasksPerLoad));
    setHasMore(filtered.length > tasksPerLoad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, search, typeFilter, categoryFilter, sortBy, showOnlyFavorites, favoriteOpportunities]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const res = await api.get('/tasks');
      const tasksData = Array.isArray(res.data) ? res.data : [];
      setTasks(shuffleArray(tasksData));
      
      // Extraire les catégories disponibles
      const categories = [...new Set(tasksData.map(t => t.project_categorie).filter(Boolean))];
      setAvailableCategories(categories);
    } catch (err) {
      logger.error(err);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMyApplications = async () => {
    try {
      const res = await api.get('/applications/my');
      setMyApplications(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      logger.error(err);
      setMyApplications([]);
    }
  };

  const getApplicationStatus = (taskId) => {
    const app = myApplications.find(a => a.task_id === taskId);
    if (!app) return null;
    return app.statut;
  };

  const getTaskTypeInfo = (type) => {
    const types = {
      collaboration: { icon: <Handshake size={14} />, label: 'Associé', color: 'bg-purple-100 text-purple-700', gradient: 'from-purple-500 to-violet-500' },
      prestation: { icon: <Coins size={14} />, label: 'Freelance', color: 'bg-green-100 text-green-700', gradient: 'from-green-500 to-emerald-500' },
      emploi: { icon: <Briefcase size={14} />, label: 'Emploi', color: 'bg-blue-100 text-blue-700', gradient: 'from-blue-500 to-cyan-500' },
      stage: { icon: <GraduationCap size={14} />, label: 'Stage', color: 'bg-orange-100 text-orange-700', gradient: 'from-orange-500 to-amber-500' },
      partenariat: { icon: <Handshake size={14} />, label: 'Partenaire', color: 'bg-indigo-100 text-indigo-700', gradient: 'from-indigo-500 to-blue-500' }
    };
    return types[type] || { icon: <ClipboardList size={14} />, label: type || 'Autre', color: 'bg-gray-100 text-gray-700', gradient: 'from-gray-500 to-slate-500' };
  };

  const getCategoryInfo = (cat) => {
    const categories = {
      commerce: { icon: <ShoppingCart size={14} />, label: 'Commerce' },
      tech: { icon: <Laptop size={14} />, label: 'Tech' },
      services: { icon: <Wrench size={14} />, label: 'Services' },
      agriculture: { icon: <Wheat size={14} />, label: 'Agriculture' },
      immobilier: { icon: <Home size={14} />, label: 'Immobilier' },
      transport: { icon: <Truck size={14} />, label: 'Transport' },
      restauration: { icon: <UtensilsCrossed size={14} />, label: 'Food' },
      mode: { icon: <Scissors size={14} />, label: 'Mode' },
      sante: { icon: <Pill size={14} />, label: 'Santé' },
      education: { icon: <BookOpen size={14} />, label: 'Éducation' },
      finance: { icon: <Coins size={14} />, label: 'Finance' },
      autre: { icon: <Folder size={14} />, label: 'Autre' }
    };
    return categories[cat] || categories.autre;
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600 mb-4"></div>
        <p className="text-indigo-600 font-medium">Chargement des opportunités...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">

        {/* Modal Devenir Auteur */}
        {showAuthorModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 p-6 rounded-t-2xl">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-4xl">
                    <Rocket size={32} />
                  </div>
                  <div className="text-white">
                    <h2 className="text-2xl font-bold">Devenez Auteur</h2>
                    <p className="text-white/80">Débloquez l'Espace Business</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <p className="text-gray-700 mb-4">
                  L'<span className="font-semibold text-purple-700">Espace Business</span> est réservé aux <span className="font-semibold">Auteurs</span>. 
                </p>

                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 mb-6">
                  <h3 className="font-bold text-purple-800 mb-3 flex items-center gap-1"><Sparkles size={16} /> Ce que vous débloquez :</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2"><span className="text-green-500"><Check size={14} /></span><span><strong>Créer des projets</strong> et recruter</span></li>
                    <li className="flex items-start gap-2"><span className="text-green-500"><Check size={14} /></span><span><strong>Gestion clients</strong>, factures, devis</span></li>
                    <li className="flex items-start gap-2"><span className="text-green-500"><Check size={14} /></span><span><strong>Suivi des paiements</strong> et créances</span></li>
                    <li className="flex items-start gap-2"><span className="text-green-500"><Check size={14} /></span><span><strong>Proposer des services</strong> professionnels</span></li>
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/profile"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                    onClick={() => setShowAuthorModal(false)}
                  >
                    <User size={16} className="inline" /> Aller sur mon Profil
                  </Link>
                  <button
                    onClick={() => setShowAuthorModal(false)}
                    className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition"
                  >
                    Plus tard
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Partage */}
        {shareModal.show && shareModal.task && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShareModal({ show: false, task: null })}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-5 text-white">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <Share2 size={20} /> Partager cette opportunité
                  </h3>
                  <button 
                    onClick={() => setShareModal({ show: false, task: null })}
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition"
                  >
                    <X size={16} />
                  </button>
                </div>
                <p className="text-white/80 text-sm mt-2 truncate">{shareModal.task.titre}</p>
              </div>
              
              {/* Contenu */}
              <div className="p-5">
                {/* Boutons de partage */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <button
                    onClick={() => shareOnWhatsApp(shareModal.task)}
                    className="flex flex-col items-center gap-2 p-4 bg-green-50 hover:bg-green-100 rounded-xl transition group"
                  >
                    <svg className="w-8 h-8 text-green-500 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    <span className="text-xs font-medium text-green-700">WhatsApp</span>
                  </button>
                  
                  <button
                    onClick={() => shareOnTelegram(shareModal.task)}
                    className="flex flex-col items-center gap-2 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition group"
                  >
                    <svg className="w-8 h-8 text-blue-500 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                    <span className="text-xs font-medium text-blue-700">Telegram</span>
                  </button>
                  
                  <button
                    onClick={() => shareByEmail(shareModal.task)}
                    className="flex flex-col items-center gap-2 p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition group"
                  >
                    <svg className="w-8 h-8 text-gray-600 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    <span className="text-xs font-medium text-gray-700">Email</span>
                  </button>
                  
                  <button
                    onClick={() => shareOnFacebook(shareModal.task)}
                    className="flex flex-col items-center gap-2 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition group"
                  >
                    <svg className="w-8 h-8 text-blue-600 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    <span className="text-xs font-medium text-blue-700">Facebook</span>
                  </button>
                  
                  <button
                    onClick={() => shareOnTwitter(shareModal.task)}
                    className="flex flex-col items-center gap-2 p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition group"
                  >
                    <svg className="w-8 h-8 text-black group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    <span className="text-xs font-medium text-gray-800">X (Twitter)</span>
                  </button>
                  
                  <button
                    onClick={() => shareOnLinkedIn(shareModal.task)}
                    className="flex flex-col items-center gap-2 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition group"
                  >
                    <svg className="w-8 h-8 text-blue-700 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    <span className="text-xs font-medium text-blue-800">LinkedIn</span>
                  </button>
                </div>
                
                {/* Copier le lien */}
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-500 mb-2">Ou copier le lien :</p>
                  <div className="flex gap-2">
                    <div className="flex-1 px-4 py-3 bg-gray-100 rounded-xl text-sm text-gray-600 truncate">
                      {getShareUrl(shareModal.task)}
                    </div>
                    <button
                      onClick={() => copyShareLink(shareModal.task)}
                      className={`px-4 py-3 rounded-xl font-semibold transition ${
                        copySuccess 
                          ? 'bg-green-500 text-white' 
                          : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                    >
                      {copySuccess ? <><Check size={14} /> Copié !</> : <><Copy size={14} /> Copier</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        

        {/* Actions rapides - Design compact */}
        <div className="mb-8">
          {/* Raccourcis principaux en scroll horizontal moderne */}
          <div className="flex gap-3 overflow-x-auto pb-3 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap snap-x snap-mandatory scroll-smooth" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
            <Link to="/author" className="flex-shrink-0 snap-start flex items-center gap-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl px-5 py-3.5 shadow-lg shadow-purple-500/20 hover:shadow-xl hover:shadow-purple-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200">
              <span className="text-2xl"><Plus size={20} /></span>
              <div>
                <div className="font-semibold text-sm whitespace-nowrap">Créer un projet</div>
                <div className="text-[10px] text-white/80 hidden sm:block">Lancer mon idée</div>
              </div>
            </Link>

            <Link to="/services" className="flex-shrink-0 snap-start flex items-center gap-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-2xl px-5 py-3.5 shadow-lg shadow-orange-500/20 hover:shadow-xl hover:shadow-orange-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200">
              <span className="text-2xl"><Briefcase size={20} /></span>
              <div>
                <div className="font-semibold text-sm whitespace-nowrap">Services</div>
                <div className="text-[10px] text-white/80 hidden sm:block">Pros & freelances</div>
              </div>
            </Link>
            
            <Link to="/boutiques" className="flex-shrink-0 snap-start flex items-center gap-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-2xl px-5 py-3.5 shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:shadow-emerald-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200">
              <span className="text-2xl"><Store size={20} /></span>
              <div>
                <div className="font-semibold text-sm whitespace-nowrap">Boutiques</div>
                <div className="text-[10px] text-white/80 hidden sm:block">Shopping en ligne</div>
              </div>
            </Link>

            {canAccessWorkspace ? (
              <Link to="/showcase" className="flex-shrink-0 snap-start flex items-center gap-3 bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-2xl px-5 py-3.5 shadow-lg shadow-indigo-500/20 hover:shadow-xl hover:shadow-indigo-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200">
                <span className="text-2xl"><Globe size={20} /></span>
                <div>
                  <div className="font-semibold text-sm whitespace-nowrap">Sites vitrine</div>
                  <div className="text-[10px] text-white/80 hidden sm:block">Créer mon site</div>
                </div>
              </Link>
            ) : (
              <button onClick={handleWorkspaceClick} className="flex-shrink-0 snap-start flex items-center gap-3 bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-2xl px-5 py-3.5 shadow-lg shadow-indigo-500/20 hover:shadow-xl hover:shadow-indigo-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 opacity-80">
                <span className="text-2xl"><Lock size={20} /></span>
                <div>
                  <div className="font-semibold text-sm whitespace-nowrap">Sites vitrine</div>
                  <div className="text-[10px] text-white/80 hidden sm:block">Auteurs uniquement</div>
                </div>
              </button>
            )}

            {/* Bouton Modules */}
            {user && modules.length > 0 && (
              <button 
                onClick={() => setShowModulesPanel(true)} 
                className="flex-shrink-0 snap-start flex items-center gap-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-2xl px-5 py-3.5 shadow-lg shadow-violet-500/20 hover:shadow-xl hover:shadow-violet-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
              >
                <span className="text-2xl"><Puzzle size={20} /></span>
                <div>
                  <div className="font-semibold text-sm whitespace-nowrap">Modules</div>
                  <div className="text-[10px] text-white/80 hidden sm:block">{modules.length} disponible{modules.length > 1 ? 's' : ''}</div>
                </div>
              </button>
            )}

            <Link to="/my-subscription" className="flex-shrink-0 snap-start flex items-center gap-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl px-5 py-3.5 shadow-lg shadow-amber-500/20 hover:shadow-xl hover:shadow-amber-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200">
              <span className="text-2xl"><Star size={20} /></span>
              <div>
                <div className="font-semibold text-sm whitespace-nowrap">Abonnement</div>
                <div className="text-[10px] text-white/80 hidden sm:block">
                  {canAccessWorkspace ? 'Gérer mon plan' : 'Devenir Auteur'}
                </div>
              </div>
            </Link>
            
            <Link to="/my-applications" className="flex-shrink-0 snap-start flex items-center gap-3 bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-700 rounded-2xl px-5 py-3.5 shadow-md hover:shadow-lg hover:border-indigo-300 hover:bg-white active:scale-[0.98] transition-all duration-200">
              <span className="text-2xl"><ClipboardList size={20} /></span>
              <div>
                <div className="font-semibold text-sm whitespace-nowrap">Candidatures</div>
                <div className="text-[10px] text-gray-500">{myApplications.length} envoyées</div>
              </div>
            </Link>
            
            
          </div>
          
          {/* Indicateur de scroll sur mobile */}
          <div className="flex justify-center gap-1 mt-2 md:hidden">
            <div className="w-8 h-1 bg-indigo-400 rounded-full"></div>
            <div className="w-2 h-1 bg-gray-300 rounded-full"></div>
            <div className="w-2 h-1 bg-gray-300 rounded-full"></div>
          </div>
        </div>

        {/* Bannière Devenir Auteur */}
        {user && user.role === 'user' && (
          <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 border border-amber-200 rounded-2xl p-6 mb-8 shadow-md">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-shrink-0"><Rocket size={48} className="text-amber-600" /></div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-amber-800 mb-2">Vous avez un projet en tête ?</h3>
                <p className="text-amber-700 mb-2">
                  Devenez <span className="font-semibold">Auteur</span> et créez vos propres projets, recrutez des talents !
                </p>
              </div>
              <Link to="/profile" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all">
                Devenir Auteur →
              </Link>
            </div>
          </div>
        )}

        {/* Barre de recherche sticky */}
        <div className="sticky top-16 z-30 -mx-4 px-4 md:mx-0 md:px-0 py-2 bg-gradient-to-b from-gray-100 via-gray-100 to-transparent">
          <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-lg p-3 md:p-4 border border-gray-100">
            <div className="flex flex-col gap-3">
              {/* Ligne 1: Recherche + bouton filtres mobile */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><Search size={16} /></span>
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm"
                  />
                  {search && (
                    <button 
                      onClick={() => setSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                
                {/* Bouton Favoris */}
                <button
                  onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-all ${
                    showOnlyFavorites
                      ? 'bg-red-50 border-red-300 text-red-600'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-500'
                  }`}
                  title="Voir mes favoris"
                >
                  <span>{showOnlyFavorites ? <Heart size={16} className="fill-red-500 text-red-500" /> : <Heart size={16} />}</span>
                  <span className="hidden sm:inline text-sm font-medium">
                    {showOnlyFavorites ? 'Favoris' : 'Favoris'}
                  </span>
                  {favoriteOpportunities.length > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      showOnlyFavorites ? 'bg-red-200 text-red-700' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {favoriteOpportunities.length}
                    </span>
                  )}
                </button>
                
                {/* Bouton filtres - visible seulement sur mobile */}
                <button 
                  onClick={() => setShowFilters(!showFilters)}
                  className={`md:hidden flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-all ${
                    showFilters || typeFilter !== 'all' || categoryFilter !== 'all'
                      ? 'bg-indigo-50 border-indigo-300 text-indigo-600'
                      : 'bg-white border-gray-200 text-gray-600'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  {(typeFilter !== 'all' || categoryFilter !== 'all') && (
                    <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                  )}
                </button>

                {/* Vue grid/list - toujours visible */}
                <div className="hidden sm:flex bg-gray-100 rounded-xl p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`px-3 py-1.5 rounded-lg transition text-sm ${viewMode === 'grid' ? 'bg-white shadow text-indigo-600' : 'text-gray-500'}`}
                  ><LayoutGrid size={14} /></button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 py-1.5 rounded-lg transition text-sm ${viewMode === 'list' ? 'bg-white shadow text-indigo-600' : 'text-gray-500'}`}
                  ><Menu size={14} /></button>
                </div>
              </div>

              {/* Filtres - toujours visibles sur desktop, toggle sur mobile */}
              <div className={`${showFilters ? 'flex' : 'hidden'} md:flex flex-col md:flex-row gap-2`}>
                {/* Filtre par type d'offre */}
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white text-sm"
                >
                  <option value="all">Type d'offre</option>
                  <option value="collaboration">Associé</option>
                  <option value="prestation">Freelance</option>
                  <option value="emploi">Emploi</option>
                  <option value="stage">Stage</option>
                  <option value="partenariat">Partenariat</option>
                </select>

                {/* Filtre par catégorie */}
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white text-sm"
                >
                  <option value="all">Catégorie</option>
                  {availableCategories.map(cat => {
                    const catInfo = getCategoryInfo(cat);
                    return (
                      <option key={cat} value={cat}>{catInfo.label}</option>
                    );
                  })}
                </select>

                {/* Tri */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white text-sm"
                >
                  <option value="random">Aléatoire</option>
                  <option value="recent">Récents</option>
                  <option value="oldest">Anciens</option>
                </select>

                {/* Vue - seulement sur mobile dans les filtres */}
                <div className="flex sm:hidden bg-gray-100 rounded-xl p-1 self-start">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`px-3 py-1.5 rounded-lg transition text-sm ${viewMode === 'grid' ? 'bg-white shadow text-indigo-600' : 'text-gray-500'}`}
                  ><LayoutGrid size={14} /> Grille</button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 py-1.5 rounded-lg transition text-sm ${viewMode === 'list' ? 'bg-white shadow text-indigo-600' : 'text-gray-500'}`}
                  ><Menu size={14} /> Liste</button>
                </div>
              </div>

              {/* Filtres actifs - compact */}
              {(search || typeFilter !== 'all' || categoryFilter !== 'all' || showOnlyFavorites) && (
                <div className="flex flex-wrap items-center gap-1.5">
                  {showOnlyFavorites && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">
                      <Heart size={12} className="fill-red-500" /> Favoris ({favoriteOpportunities.length})
                      <button onClick={() => setShowOnlyFavorites(false)} className="hover:text-red-900"><X size={10} /></button>
                    </span>
                  )}
                  {search && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs">
                      "{search.substring(0, 15)}{search.length > 15 ? '...' : ''}"
                      <button onClick={() => setSearch('')} className="hover:text-indigo-900"><X size={10} /></button>
                    </span>
                  )}
                  {typeFilter !== 'all' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                      {getTaskTypeInfo(typeFilter).icon}
                      <button onClick={() => setTypeFilter('all')} className="hover:text-purple-900"><X size={10} /></button>
                    </span>
                  )}
                  {categoryFilter !== 'all' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full text-xs">
                      {getCategoryInfo(categoryFilter).icon}
                      <button onClick={() => setCategoryFilter('all')} className="hover:text-teal-900"><X size={10} /></button>
                    </span>
                  )}
                  <button 
                    onClick={() => { setSearch(''); setTypeFilter('all'); setCategoryFilter('all'); setShowOnlyFavorites(false); }}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    Effacer
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Compteur */}
        <div className="mb-6 text-gray-600 flex items-center justify-between">
          <span>
            <span className="font-bold text-indigo-600">{filteredTasks.length}</span> {showOnlyFavorites ? 'favori(s)' : 'opportunité(s)'}
            {displayedTasks.length < filteredTasks.length && (
              <span className="text-gray-400 ml-2">• {displayedTasks.length} affichée(s)</span>
            )}
          </span>
          {sortBy === 'random' && (
            <button 
              onClick={() => setTasks(shuffleArray([...tasks]))}
              className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
            >
                            <RefreshCw size={14} /> Mélanger
            </button>
          )}
        </div>

        {/* Liste des offres */}
        {displayedTasks.length > 0 ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayedTasks.map((task, index) => {
                const imageUrl = task.image_couverture ? `${UPLOADS_URL}projects/${task.image_couverture}` : null;
                const appStatus = getApplicationStatus(task.id);
                const isLast = index === displayedTasks.length - 1;
                const typeInfo = getTaskTypeInfo(task.type);
                
                return (
                  <div 
                    key={task.id} 
                    ref={isLast ? lastTaskRef : null}
                    className={`bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group ${appStatus === 'acceptee' ? 'ring-2 ring-green-500' : ''}`}
                  >
                    {/* Image */}
                    <div className="relative h-44 overflow-hidden">
                      <ProjectImage
                        src={imageUrl}
                        alt={task.project_titre || 'Projet'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        fallbackIcon={typeInfo.icon}
                        fallbackGradient={`bg-gradient-to-br ${typeInfo.gradient}`}
                      />
                      {/* Badges */}
                      <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${typeInfo.color} backdrop-blur-sm`}>
                          {typeInfo.icon} {typeInfo.label}
                        </span>
                      </div>
                      
                      {/* Boutons Favori et Partage */}
                      <div className="absolute top-3 right-3 flex gap-2">
                        {appStatus === 'acceptee' && (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-500 text-white"><CheckCircle2 size={12} className="inline" /> Assigné</span>
                        )}
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavoriteOpportunity(task.id); }}
                          className={`w-9 h-9 rounded-full flex items-center justify-center transition-all shadow-lg ${
                            isOpportunityFavorite(task.id)
                              ? 'bg-red-500 text-white scale-110'
                              : 'bg-white/90 backdrop-blur-sm text-gray-600 hover:bg-white hover:text-red-500'
                          }`}
                          title={isOpportunityFavorite(task.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                        >
                          {isOpportunityFavorite(task.id) ? <Heart size={16} className="fill-red-500 text-red-500" /> : <Heart size={16} />}
                        </button>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShareModal({ show: true, task }); }}
                          className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm text-gray-600 flex items-center justify-center hover:bg-indigo-500 hover:text-white transition-all shadow-lg"
                          title="Partager"
                        >
                          <Share2 size={14} />
                        </button>
                      </div>
                      {/* Rémunération / Parts badges */}
                      {(task.remuneration || task.valeur_parts > 0) && (
                        <div className="absolute bottom-3 right-3 flex gap-2">
                          {task.remuneration && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-500 text-white shadow-lg">
                              <Coins size={12} className="inline" /> {Number(task.remuneration).toLocaleString()} XOF
                            </span>
                          )}
                          {task.valeur_parts > 0 && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-500 text-white shadow-lg">
                              <TrendingUp size={12} className="inline" /> {task.valeur_parts}% parts
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Contenu */}
                    <div className="p-5">
                      {/* Projet */}
                      <Link to={`/project/${task.project_id}`} className="flex items-center gap-2 mb-2 group/project">
                        <span className="text-indigo-600"><Folder size={14} /></span>
                        <span className="text-sm font-medium text-indigo-600 truncate group-hover/project:underline">{task.project_titre || 'Projet'}</span>
                      </Link>

                      {/* Titre */}
                      <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2 group-hover:text-indigo-600 transition">
                        {task.titre}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{task.description || 'Aucune description'}</p>

                      {/* Compétences */}
                      {task.competences_requises && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Target size={12} /> Compétences :</p>
                          <p className="text-sm text-blue-700 font-medium line-clamp-1">{task.competences_requises}</p>
                        </div>
                      )}

                      {/* Auteur & Date */}
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-4 pt-3 border-t">
                        <div className="flex items-center gap-2">
                          {task.photo_profil ? (
                            <img src={`${BACKEND_URL}/${task.photo_profil}`} alt="" className="w-6 h-6 rounded-full object-cover" />
                          ) : (
                            <span className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-semibold text-xs">
                              {task.author_nom?.[0] || 'A'}
                            </span>
                          )}
                          <span className="truncate max-w-[100px]">{task.author_nom || 'Auteur'}</span>
                        </div>
                        <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(task.date_creation || Date.now()).toLocaleDateString('fr-FR')}</span>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Link to={`/project/${task.project_id}?task=${task.id}`} className="flex-1 px-3 py-2.5 bg-orange-400 text-white rounded-xl font-medium hover:bg-gray-200 transition text-center text-sm">
                          Ouvrir le projet
                        </Link>
                        {!user ? (
                          <Link to="/login" className="flex-1 px-3 py-2.5 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition text-center text-sm">
                            Connexion
                          </Link>
                        ) : appStatus === 'acceptee' ? (
                          <span className="flex-1 px-3 py-2.5 bg-green-100 text-green-700 rounded-xl font-semibold text-center text-sm flex items-center justify-center gap-1"><CheckCircle size={14} /> Assigné</span>
                        ) : appStatus === 'en_attente' ? (
                          <span className="flex-1 px-3 py-2.5 bg-yellow-100 text-yellow-700 rounded-xl font-semibold text-center text-sm flex items-center justify-center gap-1"><Clock size={14} /> En attente</span>
                        ) : appStatus === 'refusee' ? (
                          <span className="flex-1 px-3 py-2.5 bg-red-100 text-red-700 rounded-xl font-semibold text-center text-sm flex items-center justify-center gap-1"><XCircle size={14} /> Refusée</span>
                        ) : (
                          <Link to={`/apply/${task.id}`} className="flex-1 px-3 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition text-center text-sm flex items-center justify-center gap-1">
                            <Rocket size={14} /> Postuler
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Vue Liste */
            <div className="space-y-4">
              {displayedTasks.map((task, index) => {
                const imageUrl = task.image_couverture ? `${UPLOADS_URL}projects/${task.image_couverture}` : null;
                const appStatus = getApplicationStatus(task.id);
                const isLast = index === displayedTasks.length - 1;
                const typeInfo = getTaskTypeInfo(task.type);
                
                return (
                  <div 
                    key={task.id} 
                    ref={isLast ? lastTaskRef : null}
                    className={`bg-white rounded-2xl shadow-md hover:shadow-xl transition p-5 ${appStatus === 'acceptee' ? 'ring-2 ring-green-500' : ''}`}
                  >
                    <div className="flex flex-col md:flex-row gap-5">
                      {/* Image */}
                      <div className="w-full md:w-56 h-40 rounded-xl overflow-hidden flex-shrink-0 relative">
                        <ProjectImage
                          src={imageUrl}
                          alt={task.project_titre || 'Projet'}
                          className="w-full h-full object-cover"
                          fallbackIcon={typeInfo.icon}
                          fallbackGradient={`bg-gradient-to-br ${typeInfo.gradient}`}
                        />
                        {/* Boutons Favori et Partage pour vue liste */}
                        <div className="absolute top-2 right-2 flex gap-1.5">
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavoriteOpportunity(task.id); }}
                            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all shadow-md ${
                              isOpportunityFavorite(task.id)
                                ? 'bg-red-500 text-white'
                                : 'bg-white/90 backdrop-blur-sm text-gray-600 hover:bg-white hover:text-red-500'
                            }`}
                            title={isOpportunityFavorite(task.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                          >
                            {isOpportunityFavorite(task.id) ? <Heart size={16} className="fill-red-500 text-red-500" /> : <Heart size={16} />}
                          </button>
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShareModal({ show: true, task }); }}
                            className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm text-gray-600 flex items-center justify-center hover:bg-indigo-500 hover:text-white transition-all shadow-md"
                            title="Partager"
                          >
                            <Share2 size={14} />
                          </button>
                        </div>
                        {(task.remuneration || task.valeur_parts > 0) && (
                          <div className="absolute bottom-2 right-2 flex gap-1">
                            {task.remuneration && (
                              <span className="px-2 py-1 rounded-full text-xs font-bold bg-green-500 text-white">
                                <Coins size={12} className="inline" /> {Number(task.remuneration).toLocaleString()} XOF
                              </span>
                            )}
                            {task.valeur_parts > 0 && (
                              <span className="px-2 py-1 rounded-full text-xs font-bold bg-purple-500 text-white">
                                <TrendingUp size={12} className="inline" /> {task.valeur_parts}%
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Contenu */}
                      <div className="flex-1 flex flex-col">
                        {/* Header */}
                        <div className="flex flex-wrap items-start gap-2 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${typeInfo.color}`}>
                            {typeInfo.icon} {typeInfo.label}
                          </span>
                          <Link to={`/project/${task.project_id}`} className="text-sm text-indigo-600 font-medium hover:underline flex items-center gap-1">
                            <Folder size={14} /> {task.project_titre || 'Projet'}
                          </Link>
                          {appStatus === 'acceptee' && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold flex items-center gap-1"><CheckCircle2 size={12} /> Assigné</span>
                          )}
                        </div>

                        {/* Titre */}
                        <h3 className="text-xl font-bold text-gray-800 mb-2">{task.titre}</h3>
                        
                        {/* Description */}
                        <p className="text-gray-600 mb-3 line-clamp-2 flex-grow">{task.description || 'Aucune description'}</p>

                        {/* Détails */}
                        <div className="flex flex-wrap gap-3 mb-4">
                          {task.competences_requises && (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg">
                              <span><Target size={14} /></span>
                              <span className="text-sm text-blue-700 font-medium">{task.competences_requises}</span>
                            </div>
                          )}
                        </div>

                        {/* Footer */}
                        <div className="flex flex-wrap items-center justify-between gap-4 pt-3 border-t">
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <div className="flex items-center gap-2">
                              {task.photo_profil ? (
                                <img src={`${BACKEND_URL}/${task.photo_profil}`} alt="" className="w-6 h-6 rounded-full object-cover" />
                              ) : (
                                <span className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-semibold text-xs">
                                  {task.author_nom?.[0] || 'A'}
                                </span>
                              )}
                              <span>{task.author_nom || 'Auteur'}</span>
                            </div>
                            <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(task.date_creation || Date.now()).toLocaleDateString('fr-FR')}</span>
                          </div>
                          
                          <div className="flex gap-2">
                            <Link to={`/project/${task.project_id}?task=${task.id}`} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition text-sm">
                              Voir projet
                            </Link>
                            {!user ? (
                              <Link to="/login" className="px-4 py-2 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition text-sm">
                                Connexion
                              </Link>
                            ) : appStatus === 'acceptee' ? (
                              <span className="px-4 py-2 bg-green-100 text-green-700 rounded-xl font-semibold text-sm flex items-center gap-1"><CheckCircle size={14} /> Assigné</span>
                            ) : appStatus === 'en_attente' ? (
                              <span className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-xl font-semibold text-sm flex items-center gap-1"><Clock size={14} /> En attente</span>
                            ) : appStatus === 'refusee' ? (
                              <span className="px-4 py-2 bg-red-100 text-red-700 rounded-xl font-semibold text-sm flex items-center gap-1"><XCircle size={14} /> Refusée</span>
                            ) : (
                              <Link to={`/apply/${task.id}`} className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition text-sm flex items-center gap-1">
                                <Rocket size={14} /> Postuler
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-16 text-center">
            <span className="text-8xl mb-6 block"><Search size={64} className="mx-auto text-gray-400" /></span>
            <h3 className="text-2xl font-bold text-gray-700 mb-3">Aucune opportunité trouvée</h3>
            <p className="text-gray-500 mb-6">Essayez de modifier vos filtres ou revenez plus tard !</p>
            <button
              onClick={() => { setSearch(''); setTypeFilter('all'); }}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition"
            >
              Réinitialiser les filtres
            </button>
          </div>
        )}

        {/* Loader pour scroll infini */}
        {loadingMore && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-b-4 border-indigo-600"></div>
          </div>
        )}

        {/* Message fin de liste */}
        {!hasMore && displayedTasks.length > 0 && (
          <div className="text-center py-8 text-gray-500">
            <p className="flex items-center justify-center gap-1"><Sparkles size={16} /> Vous avez tout vu !</p>
          </div>
        )}

        {/* CTA final pour auteurs */}
        {!loading && displayedTasks.length === 0 && user && (user.role === 'author' || user.role === 'admin') && (
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-center text-white shadow-xl">
            <span className="text-5xl mb-4 block"><Rocket size={48} className="mx-auto" /></span>
            <h3 className="text-2xl font-bold mb-2">Soyez le premier !</h3>
            <p className="text-white/80 mb-6">Créez votre projet et publiez vos offres</p>
            <Link to="/create-project" className="inline-block px-8 py-3 bg-white text-indigo-600 rounded-xl font-bold hover:bg-gray-100 transition">
              Créer un projet
            </Link>
          </div>
        )}
      </div>

      {/* Bouton flottant en bas à droite */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-50">
        {/* Bouton retour en haut - visible quand on scroll */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="w-14 h-14 bg-white text-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center hover:scale-110 hover:bg-gray-50 group border border-gray-200"
          title="Retour en haut"
        >
          <svg className="w-6 h-6 group-hover:-translate-y-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>

        {/* Bouton ouvrir panneau modules */}
        {user && modules.length > 0 && (
          <button
            onClick={() => setShowModulesPanel(true)}
            className="w-14 h-14 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-full shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 transition-all duration-300 flex items-center justify-center hover:scale-110 group"
            title="Mes modules"
          >
            <svg className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
        )}

        {/* Bouton Mes projets */}
        <Link
          to="/author"
          className="w-14 h-14 bg-gradient-to-r from-indigo-500 to-blue-500 text-white rounded-full shadow-lg shadow-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/40 transition-all duration-300 flex items-center justify-center hover:scale-110 group"
          title="Mes projets"
        >
          <span className="text-2xl group-hover:scale-110 transition-transform"><Rocket size={24} /></span>
        </Link>
      </div>

      {/* Boutons flottants des modules favoris en bas à gauche */}
      {user && favoriteModulesData.length > 0 && showFavorites && (
        <div className="fixed bottom-6 left-6 z-50 flex flex-col-reverse gap-2">
          {/* Toggle pour masquer/afficher les favoris */}
          <button
            onClick={() => setShowFavorites(prev => !prev)}
            className="w-10 h-10 bg-gray-800/80 backdrop-blur-sm text-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-700 transition-all duration-300 text-xs"
            title="Masquer les favoris"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {/* Liste des favoris */}
          {favoriteModulesData.map((mod, index) => (
            <Link
              key={mod.id}
              to={cleanRoutePattern(mod.route_pattern)}
              className="group relative flex items-center gap-2 px-4 py-2 bg-white/95 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-200 animate-fadeInLeft"
              style={{ animationDelay: `${index * 50}ms` }}
              title={mod.nom}
            >
              <span className="text-lg">{mod.icone || <Package size={16} />}</span>
              <span className="text-sm font-medium text-gray-700 max-w-[120px] truncate">{mod.nom}</span>
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full flex items-center justify-center shadow-md">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Bouton pour réafficher les favoris quand ils sont masqués */}
      {user && favoriteModulesData.length > 0 && !showFavorites && (
        <button
          onClick={() => setShowFavorites(true)}
          className="fixed bottom-6 left-6 z-50 w-12 h-12 bg-gradient-to-r from-amber-400 to-yellow-500 text-white rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-all duration-300"
          title="Afficher les modules favoris"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold">
            {favoriteModulesData.length}
          </span>
        </button>
      )}

      {/* Panneau overlay des modules */}
      {showModulesPanel && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fadeIn"
            onClick={() => setShowModulesPanel(false)}
          />
          
          {/* Panel */}
          <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-scaleIn max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  Tous les Modules
                </h2>
                <button
                  onClick={() => setShowModulesPanel(false)}
                  className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Search bar */}
              <div className="relative">
                <input
                  type="text"
                  value={moduleSearch}
                  onChange={(e) => setModuleSearch(e.target.value)}
                  placeholder="Rechercher un module..."
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/20 text-white placeholder-white/60 border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/30 transition-all"
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            
            {/* Liste des modules */}
            <div className="flex-1 overflow-y-auto p-4">
              {filteredModules.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <span className="text-5xl mb-4 block"><Search size={40} className="mx-auto text-gray-400" /></span>
                  <p>Aucun module trouvé</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredModules.map((mod, index) => {
                    const isFavorite = favoriteModules.includes(mod.id);
                    return (
                      <div
                        key={mod.id}
                        className="group bg-gray-50 hover:bg-white rounded-xl p-4 transition-all duration-300 hover:shadow-lg border border-gray-100 hover:border-purple-200 animate-fadeInUp"
                        style={{ animationDelay: `${index * 30}ms` }}
                      >
                        <div className="flex items-start gap-3">
                          {/* Icône module */}
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-2xl flex-shrink-0 shadow-md">
                            {mod.icone || <Package size={20} />}
                          </div>
                          
                          {/* Info module */}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-800 truncate">{mod.nom}</h3>
                            <p className="text-sm text-gray-500 line-clamp-2">{mod.description || 'Module actif'}</p>
                            
                            {/* Route pattern */}
                            {mod.route_pattern && (
                              <Link
                                to={cleanRoutePattern(mod.route_pattern)}
                                className="inline-flex items-center gap-1 mt-2 text-sm text-purple-600 hover:text-purple-700 font-medium group/link"
                                onClick={() => setShowModulesPanel(false)}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                Accéder au module
                                <svg className="w-3 h-3 opacity-0 group-hover/link:opacity-100 group-hover/link:translate-x-1 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </Link>
                            )}
                          </div>
                          
                          {/* Bouton favori */}
                          <button
                            onClick={() => toggleFavorite(mod.id)}
                            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 ${
                              isFavorite 
                                ? 'bg-gradient-to-r from-amber-400 to-yellow-500 text-white shadow-lg shadow-amber-500/30 scale-110' 
                                : 'bg-gray-200 text-gray-400 hover:bg-amber-100 hover:text-amber-500'
                            }`}
                            title={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                          >
                            <svg className={`w-5 h-5 transition-transform ${isFavorite ? 'animate-pulse' : ''}`} fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Footer info */}
            <div className="p-4 bg-gray-50 border-t border-gray-100">
              <p className="text-center text-sm text-gray-500">
                <span className="inline-flex items-center gap-1">
                  <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  {favoriteModules.length} module{favoriteModules.length > 1 ? 's' : ''} en favori{favoriteModules.length > 1 ? 's' : ''}
                </span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedPourToi;
